import { Linkedin } from "lucide-react";

const FOUNDERS = [
  {
    name: "Prinkan Pal",
    role: "Co-founder & CEO",
    linkedin: "#",
    photo: "https://images.unsplash.com/photo-1769636929231-3cd7f853d038?crop=entropy&cs=srgb&fm=jpg&q=85&w=800",
    bio: "An AI and analytics leader with a proven track record of delivering high-impact enterprise solutions. At Intugle, Prinkan specializes in building and scaling data-driven products that transform complex business challenges into scalable, contextually intelligent AI applications.",
  },
  {
    name: "Manan Pachnanda",
    role: "Co-founder & CPO",
    linkedin: "#",
    photo: "https://images.unsplash.com/photo-1769636929261-e913ed023c83?crop=entropy&cs=srgb&fm=jpg&q=85&w=800",
    bio: "A strategy and AI leader with over 12 years of experience driving business transformation for Fortune 500 organizations. At Intugle, Manan works at the intersection of enterprise strategy, data engineering, semantic intelligence, and agentic AI to deliver scalable business outcomes.",
  },
  {
    name: "Jaskaran Singh",
    role: "Co-founder & CTO",
    linkedin: "#",
    photo: "https://images.unsplash.com/photo-1765776830139-72b2184dae5a?crop=entropy&cs=srgb&fm=jpg&q=85&w=800",
    bio: "An AI and data science leader with extensive experience in machine learning, natural language processing, and enterprise intelligence. At Intugle, Jaskaran focuses on building context-aware AI systems that help organizations transform complex enterprise data into actionable knowledge and scalable AI solutions.",
  },
];

const Label = ({ children }) => (
  <div className="bg-black text-[#EDA13A] font-mono2 text-xs uppercase tracking-[0.28em] px-6 py-3">{children}</div>
);

const Head = ({ tone = "amber" }) => (
  <>
    <p className={`font-mono2 text-[11px] uppercase tracking-[0.28em] mb-4 ${tone === "amber" ? "text-[#EDA13A]" : "text-[#EDA13A]"}`}>Leadership</p>
    <h2 className="font-display font-medium tracking-tight text-2xl sm:text-3xl lg:text-[2.5rem] leading-[1.12] max-w-4xl">Meet the Founders</h2>
  </>
);

// ---------- D1: Photo-top cards ----------
export const LeadDark1 = () => (
  <section className="bg-[#211307] text-[#F7F5F0] py-20 lg:py-28" data-testid="lead-d1">
    <div className="max-w-7xl mx-auto px-6 sm:px-8">
      <Head />
      <div className="mt-12 grid md:grid-cols-3 gap-6">
        {FOUNDERS.map((f) => (
          <div key={f.name} className="rounded-2xl bg-white/[0.03] ring-1 ring-white/10 overflow-hidden flex flex-col">
            <div className="aspect-[4/5] overflow-hidden">
              <img src={f.photo} alt={f.name} className="w-full h-full object-cover grayscale-[0.15]" />
            </div>
            <div className="p-6 flex flex-col flex-1">
              <h3 className="font-display text-xl font-medium">{f.name}</h3>
              <p className="mt-1 font-mono2 text-[11px] uppercase tracking-[0.16em] text-[#EDA13A]">{f.role}</p>
              <p className="mt-4 text-[13px] text-white/60 leading-relaxed flex-1">{f.bio}</p>
              <a href={f.linkedin} className="mt-5 inline-flex items-center gap-2 text-sm font-medium text-white/80 hover:text-[#EDA13A] transition-colors"><Linkedin size={16} /> Connect</a>
            </div>
          </div>
        ))}
      </div>
    </div>
  </section>
);

// ---------- D2: Full-bleed portrait, hover-reveal bio ----------
export const LeadDark2 = () => (
  <section className="bg-[#211307] text-[#F7F5F0] py-20 lg:py-28" data-testid="lead-d2">
    <div className="max-w-7xl mx-auto px-6 sm:px-8">
      <Head />
      <div className="mt-12 grid md:grid-cols-3 gap-5">
        {FOUNDERS.map((f) => (
          <div key={f.name} className="group relative rounded-2xl overflow-hidden aspect-[3/4] ring-1 ring-white/10">
            <img src={f.photo} alt={f.name} className="absolute inset-0 w-full h-full object-cover grayscale-[0.2] group-hover:scale-105 transition-transform duration-700" />
            <div className="absolute inset-0 bg-gradient-to-t from-[#160D05] via-[#160D05]/30 to-transparent" />
            <a href={f.linkedin} className="absolute top-4 right-4 w-10 h-10 rounded-full bg-black/40 backdrop-blur ring-1 ring-white/25 flex items-center justify-center text-white hover:bg-[#EDA13A] hover:text-[#211307] transition-colors"><Linkedin size={16} /></a>
            <div className="absolute inset-x-0 bottom-0 p-6">
              <h3 className="font-display text-2xl font-medium">{f.name}</h3>
              <p className="mt-1 font-mono2 text-[11px] uppercase tracking-[0.16em] text-[#EDA13A]">{f.role}</p>
              <p className="mt-3 text-[12px] text-white/75 leading-relaxed max-h-0 opacity-0 group-hover:max-h-40 group-hover:opacity-100 transition-all duration-500 overflow-hidden">{f.bio}</p>
            </div>
          </div>
        ))}
      </div>
      <p className="mt-6 text-center text-xs text-white/40">Hover a card to reveal the bio</p>
    </div>
  </section>
);

// ---------- D3: Editorial rows, portrait left ----------
export const LeadDark3 = () => (
  <section className="bg-[#211307] text-[#F7F5F0] py-20 lg:py-28" data-testid="lead-d3">
    <div className="max-w-6xl mx-auto px-6 sm:px-8">
      <Head />
      <div className="mt-12">
        {FOUNDERS.map((f) => (
          <div key={f.name} className="grid md:grid-cols-[220px_1fr_auto] gap-6 md:gap-10 items-center py-9 border-t border-white/10">
            <div className="w-40 h-48 rounded-2xl overflow-hidden ring-1 ring-white/10">
              <img src={f.photo} alt={f.name} className="w-full h-full object-cover grayscale-[0.15]" />
            </div>
            <div>
              <h3 className="font-display text-2xl font-medium">{f.name}</h3>
              <p className="mt-1 font-mono2 text-[11px] uppercase tracking-[0.16em] text-[#EDA13A]">{f.role}</p>
              <p className="mt-4 text-sm text-white/65 leading-relaxed max-w-2xl">{f.bio}</p>
            </div>
            <a href={f.linkedin} className="shrink-0 w-11 h-11 rounded-full ring-1 ring-white/20 flex items-center justify-center text-white/70 hover:bg-[#EDA13A] hover:text-[#211307] hover:ring-[#EDA13A] transition-colors"><Linkedin size={18} /></a>
          </div>
        ))}
      </div>
    </div>
  </section>
);

export default function LeadershipDark() {
  return (
    <div>
      <Label>Option D1 — Photo-Top Cards (dark)</Label>
      <LeadDark1 />
      <Label>Option D2 — Full-Bleed Portraits, hover reveals bio (dark)</Label>
      <LeadDark2 />
      <Label>Option D3 — Editorial Rows with Portrait (dark)</Label>
      <LeadDark3 />
    </div>
  );
}
